/**
 * PositSelectionModal.tsx
 * 
 * Modal shown when users downgrade to free plan to let them choose
 * which Posit remains active (since free plan only allows 1 active Posit).
 */

import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  AlertTriangle,
  Check,
  Clock,
  Eye,
  DollarSign,
  Users
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface Posit {
  id: string;
  title: string;
  privacy: string;
  is_paid: boolean;
  subscription_price?: number;
  created_at: string;
  view_count?: number;
}

interface PositSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userPosits: Posit[];
  onSelectionComplete: (selectedPositId: string) => void;
}

const PositSelectionModal: React.FC<PositSelectionModalProps> = ({
  isOpen,
  onClose,
  userPosits,
  onSelectionComplete
}) => {
  const [selectedPositId, setSelectedPositId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleConfirmSelection = async () => {
    if (!selectedPositId) {
      toast({
        variant: "destructive",
        title: "Selection Required",
        description: "Please select which Posit to keep active",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Update database: selected Posit active, others inactive
      const { error } = await supabase.functions.invoke('manage-posit-activation', {
        body: {
          action: 'set_active_posit',
          active_posit_id: selectedPositId,
          user_posits: userPosits.map(p => p.id)
        }
      });

      if (error) throw error;

      onSelectionComplete(selectedPositId);
      toast({
        title: "Posit Selection Updated",
        description: "Your selected Posit will remain active",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update selection. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return 'Free';
    return `$${price.toFixed(2)}/m`;
  };

  const getPrivacyIcon = (privacy: string) => {
    switch (privacy) {
      case 'public': return <Eye className="h-3 w-3" />;
      case 'private': return <Users className="h-3 w-3" />;
      default: return <Eye className="h-3 w-3" />;
    }
  };

  const getPrivacyColor = (privacy: string) => {
    switch (privacy) {
      case 'public': return 'default';
      case 'private': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
          </div>
          <DialogTitle className="text-center">Choose Your Active Posit</DialogTitle>
          <DialogDescription className="text-center">
            Your plan now allows 1 active Posit. Choose which one to keep accessible.
            The others will be temporarily deactivated but not deleted.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Plan Info */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900">Free Plan Limits</span>
            </div>
            <p className="text-sm text-blue-700">
              • 1 active Posit (others temporarily deactivated)
              • Can upgrade anytime to reactivate all Posits
              • Deactivated Posits keep all their data
            </p>
          </div>

          {/* Posit Selection */}
          <div className="space-y-3 max-h-60 overflow-y-auto">
            <h4 className="font-medium">Select the Posit to keep active:</h4>
            
            {userPosits.map((posit) => (
              <Card 
                key={posit.id} 
                className={`cursor-pointer border-2 transition-all ${
                  selectedPositId === posit.id 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedPositId(posit.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {/* Selection Indicator */}
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedPositId === posit.id 
                        ? 'border-primary bg-primary' 
                        : 'border-muted-foreground'
                    }`}>
                      {selectedPositId === posit.id && (
                        <Check className="h-2.5 w-2.5 text-white" />
                      )}
                    </div>
                    
                    {/* Posit Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="font-medium truncate">{posit.title}</h3>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {format(new Date(posit.created_at), "MMM d, yyyy")}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Privacy Badge */}
                        <Badge variant={getPrivacyColor(posit.privacy)} className="text-xs px-1.5 py-0.5">
                          <div className="flex items-center gap-1">
                            {getPrivacyIcon(posit.privacy)}
                            {posit.privacy}
                          </div>
                        </Badge>
                        
                        {/* Monetization Badge */}
                        {posit.is_paid && (
                          <Badge variant="default" className="text-xs px-1.5 py-0.5">
                            <DollarSign className="h-3 w-3 mr-1" />
                            {formatPrice(posit.subscription_price)}
                          </Badge>
                        )}
                        

                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Warning */}
          <div className="bg-amber-50 p-3 rounded border border-amber-200">
            <p className="text-xs text-amber-700">
              <strong>Don't worry:</strong> Deactivated Posits keep all their content and settings. 
              You can upgrade to Posit Plus anytime to reactivate all your Posits instantly.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSelection}
              disabled={isSubmitting || !selectedPositId}
              className="flex-1"
            >
              {isSubmitting ? (
                "Updating..."
              ) : (
                "Keep This Posit Active"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PositSelectionModal; 